import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Mail, Send, FileText, Plus, Edit2, Trash2, Save, X,
  Clock, CheckCircle2, MailOpen, ChevronRight, Search, Settings, Globe, ExternalLink, Eye, MousePointerClick, Inbox, PenTool, Upload, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import EmailCompose from './EmailCompose';
import DealerInbox from './DealerInbox';
import DripCampaignPanel from './DripCampaignPanel';
import EmailHealthWidget from './dashboard/EmailHealthWidget';
import { getAuthToken } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailCenterTab = ({ isDark, leads }) => {
  const [activeSection, setActiveSection] = useState('inbox');
  const [templates, setTemplates] = useState({ defaults: [], custom: [] });
  const [recentEmails, setRecentEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadSearch, setLeadSearch] = useState('');

  // Super admin toggle: "My Leads" vs "All Leads (Global)"
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [leadScope, setLeadScope] = useState('mine'); // 'mine' | 'global'
  const [globalLeads, setGlobalLeads] = useState([]);
  const [loadingGlobalLeads, setLoadingGlobalLeads] = useState(false);

  // Template editing
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Pre-loaded template for compose
  const [preloadedTemplate, setPreloadedTemplate] = useState(null);

  useEffect(() => {
    fetchData();
    fetchUnreadCount();
    // Detect super admin from cached user object so we can show the global toggle
    try {
      // Never treat an impersonation session as super admin — the token is the
      // advisor's even though localStorage.user is still the admin object.
      const impersonating = localStorage.getItem('impersonating') === 'true';
      const raw = localStorage.getItem('user');
      const u = raw ? JSON.parse(raw) : null;
      if (!impersonating && u && (u.email || '').toLowerCase() === 'malleg85@gmail.com') {
        setIsSuperAdmin(true);
      }
    } catch { /* ignore */ }
  }, []);

  // Lazy-load global leads only when SA flips the toggle (and once per session)
  useEffect(() => {
    if (leadScope !== 'global' || !isSuperAdmin || globalLeads.length > 0) return;
    const run = async () => {
      setLoadingGlobalLeads(true);
      try {
        const token = getAuthToken();
        const res = await axios.get(`${API}/admin/all-leads`, { headers: { Authorization: `Bearer ${token}` } });
        setGlobalLeads(res.data.leads || []);
      } catch (err) {
        console.error('Global leads fetch failed:', err);
        toast.error('Could not load global leads');
        setLeadScope('mine');
      } finally {
        setLoadingGlobalLeads(false);
      }
    };
    run();
  }, [leadScope, isSuperAdmin, globalLeads.length]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      const [templatesRes, emailsRes] = await Promise.all([
        axios.get(`${API}/dealer/email-templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dealer/emails/recent`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setTemplates(templatesRes.data);
      setRecentEmails(emailsRes.data);
    } catch (err) {
      console.error('Error loading email center:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/inbox/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadCount(res.data.unread_count);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  const activeLeadsList = leadScope === 'global' ? globalLeads : (leads || []);
  const filteredLeads = activeLeadsList.filter(l =>
    !leadSearch || l.email?.toLowerCase().includes(leadSearch.toLowerCase()) || l.name?.toLowerCase().includes(leadSearch.toLowerCase())
  );

  const handleSaveTemplate = async () => {
    const tmpl = newTemplate || editingTemplate;
    if (!tmpl?.name?.trim() || !tmpl?.subject?.trim() || !tmpl?.body?.trim()) {
      toast.error('Please fill in all template fields');
      return;
    }
    setIsSavingTemplate(true);
    try {
      const token = getAuthToken();
      if (newTemplate) {
        await axios.post(`${API}/dealer/email-templates`, {
          name: tmpl.name,
          subject: tmpl.subject,
          body: tmpl.body,
          category: tmpl.category || 'custom',
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Template created');
      } else {
        await axios.put(`${API}/dealer/email-templates/${tmpl.id}`, {
          name: tmpl.name,
          subject: tmpl.subject,
          body: tmpl.body,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Template updated');
      }
      setNewTemplate(null);
      setEditingTemplate(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/dealer/email-templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Template deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const handleUseTemplate = (template) => {
    setPreloadedTemplate(template);
    setActiveSection('compose');
    toast.info(`Template "${template.name}" loaded. Select a lead to send to.`);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="email-center-loading">
        <Loader2 className="animate-spin text-[#004E86]" size={36} />
      </div>
    );
  }

  return (
    <div data-testid="email-center-tab" className={`flex flex-col lg:flex-row gap-3 -mx-2 sm:-mx-0 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} rounded-2xl overflow-hidden min-h-[70vh]`}>
      {/* Gmail-style left sidebar */}
      <aside className={`lg:w-56 shrink-0 ${isDark ? 'bg-gray-800' : 'bg-white'} lg:rounded-l-2xl border-b lg:border-b-0 lg:border-r ${isDark ? 'border-gray-700' : 'border-gray-200'} p-3 lg:p-4`}>
        {/* Compose pill button */}
        <button
          onClick={() => setActiveSection('compose')}
          className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-[#c2410c] hover:bg-[#9a3412] text-white font-semibold text-sm w-full lg:w-auto shadow-sm transition-colors"
          data-testid="compose-tab"
        >
          <PenTool size={16} />
          Compose
        </button>

        {/* Folder list */}
        <nav className="mt-4 flex lg:flex-col gap-2 lg:gap-1 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 -mx-1 px-1 lg:mx-0 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <FolderButton
            id="inbox"
            label="Inbox"
            active={activeSection === 'inbox'}
            isDark={isDark}
            onClick={() => setActiveSection('inbox')}
            badge={unreadCount}
          >
            <Inbox size={16} className="shrink-0" />
          </FolderButton>
          {isSuperAdmin && (
            <FolderButton
              id="advisor-inbox"
              label="Advisor / Mike"
              active={activeSection === 'advisor-inbox'}
              isDark={isDark}
              onClick={() => setActiveSection('advisor-inbox')}
            >
              <Inbox size={16} className="shrink-0" />
            </FolderButton>
          )}
          {isSuperAdmin && (
            <FolderButton
              id="drip"
              label="Auto Drip (30-Day)"
              active={activeSection === 'drip'}
              isDark={isDark}
              onClick={() => setActiveSection('drip')}
            >
              <Clock size={16} className="shrink-0" />
            </FolderButton>
          )}
          <FolderButton
            id="history"
            label="Sent"
            active={activeSection === 'history'}
            isDark={isDark}
            onClick={() => setActiveSection('history')}
          >
            <Send size={16} className="shrink-0" />
          </FolderButton>
          <FolderButton
            id="templates"
            label="Templates"
            active={activeSection === 'templates'}
            isDark={isDark}
            onClick={() => setActiveSection('templates')}
          >
            <FileText size={16} className="shrink-0" />
          </FolderButton>
          <FolderButton
            id="signature"
            label="Signature"
            active={activeSection === 'signature'}
            isDark={isDark}
            onClick={() => setActiveSection('signature')}
          >
            <PenTool size={16} className="shrink-0" />
          </FolderButton>
          {isSuperAdmin && (
            <FolderButton
              id="settings"
              label="Domain"
              active={activeSection === 'settings'}
              isDark={isDark}
              onClick={() => setActiveSection('settings')}
            >
              <Globe size={16} className="shrink-0" />
            </FolderButton>
          )}
        </nav>

        {/* Compact health widget */}
        <div className="hidden lg:block mt-6">
          <EmailHealthWidget isDark={isDark} />
        </div>
      </aside>

      {/* Main pane — list / reading view / compose */}
      <main className={`flex-1 ${isDark ? 'bg-gray-900' : 'bg-white'} lg:rounded-r-2xl p-3 sm:p-5 min-w-0`}>
        {/* Mobile-only health widget compact */}
        <div className="lg:hidden mb-4">
          <EmailHealthWidget isDark={isDark} />
        </div>

        {/* Inbox Section */}
        {activeSection === 'inbox' && (
          <DealerInbox isDark={isDark} onUnreadUpdate={setUnreadCount} filter="regular" />
        )}
        {activeSection === 'advisor-inbox' && isSuperAdmin && (
          <DealerInbox isDark={isDark} filter="platform" />
        )}

        {/* Compose Section */}
        {activeSection === 'compose' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Lead Selector */}
            <div className={`md:col-span-1 rounded-xl p-4 ${isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'} shadow-sm`}>
              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Select Lead</h3>

              {/* Super-admin only: scope toggle between own dealer leads and the global all-leads list */}
              {isSuperAdmin && (
                <div className={`flex gap-1 p-1 mb-3 rounded-lg ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`} data-testid="leads-scope-toggle">
                  <button
                    type="button"
                    onClick={() => setLeadScope('mine')}
                    className={`flex-1 text-xs font-bold py-1.5 rounded-md transition-colors ${
                      leadScope === 'mine'
                        ? (isDark ? 'bg-[#F2A900] text-black' : 'bg-white text-gray-900 shadow')
                        : (isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900')
                    }`}
                    data-testid="leads-scope-mine"
                  >
                    My Leads ({(leads || []).length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setLeadScope('global')}
                    className={`flex-1 text-xs font-bold py-1.5 rounded-md transition-colors flex items-center justify-center gap-1 ${
                      leadScope === 'global'
                        ? (isDark ? 'bg-[#F2A900] text-black' : 'bg-white text-gray-900 shadow')
                        : (isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900')
                    }`}
                    data-testid="leads-scope-global"
                  >
                    <Globe size={11} /> All Leads {globalLeads.length > 0 ? `(${globalLeads.length})` : '(Global)'}
                  </button>
                </div>
              )}

              <div className="relative mb-3">
                <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <Input
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  placeholder="Search leads..."
                  className={`pl-8 h-8 text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-400' : ''}`}
                  data-testid="lead-search-input"
                />
              </div>
              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                {loadingGlobalLeads && (
                  <div className="flex items-center gap-2 p-3">
                    <Loader2 size={14} className="animate-spin" />
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading global leads…</span>
                  </div>
                )}
                {filteredLeads.map((lead) => (
                  <button
                  key={lead.id}
                  onClick={() => setSelectedLead(lead)}
                  className={`w-full text-left p-2.5 rounded-lg transition-colors text-sm ${
                    selectedLead?.id === lead.id
                      ? isDark ? 'bg-[#004E86]/30 border border-[#004E86]' : 'bg-blue-50 border border-[#004E86]'
                      : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`select-lead-${lead.id}`}
                >
                  <p className={`font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {lead.name || 'No name'}
                  </p>
                  <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{lead.email}</p>
                </button>
              ))}
              {filteredLeads.length === 0 && (
                <p className={`text-sm text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No leads found</p>
              )}
            </div>
          </div>

          {/* Compose Area */}
          <div className={`md:col-span-2 rounded-xl p-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
            {/* Show loaded template info */}
            {preloadedTemplate && (
              <div className={`mb-4 p-3 rounded-lg border-l-4 border-[#F2A900] ${isDark ? 'bg-gray-700/50' : 'bg-amber-50'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs font-semibold ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>Template Loaded</p>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{preloadedTemplate.name}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setPreloadedTemplate(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </Button>
                </div>
              </div>
            )}
            
            {selectedLead ? (
              <>
                <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Compose Email to {selectedLead.name || selectedLead.email}
                </h3>
                <EmailCompose
                  leadId={selectedLead.id}
                  leadEmail={selectedLead.email}
                  leadName={selectedLead.name}
                  isDark={isDark}
                  preloadedTemplate={preloadedTemplate}
                  onSent={() => { fetchData(); setPreloadedTemplate(null); }}
                />
              </>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Mail size={48} className={`mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                    {preloadedTemplate ? 'Now select a lead to send this template to' : 'Select a lead to compose an email'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Templates Section */}
      {activeSection === 'templates' && (
        <div className="space-y-6">
          {/* Create Template Button */}
          <div className="flex justify-end">
            <Button
              onClick={() => setNewTemplate({ name: '', subject: '', body: '', category: 'custom' })}
              className="bg-[#004E86] hover:bg-[#003d6b] text-white flex items-center gap-2"
              data-testid="create-template-btn"
            >
              <Plus size={16} /> New Template
            </Button>
          </div>

          {/* New/Edit Template Form */}
          {(newTemplate || editingTemplate) && (
            <div className={`rounded-xl p-5 border-2 ${isDark ? 'bg-gray-800 border-[#004E86]' : 'bg-white border-[#004E86]'} shadow-sm`} data-testid="template-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {newTemplate ? 'New Template' : 'Edit Template'}
                </h3>
                <Button variant="ghost" size="sm" onClick={() => { setNewTemplate(null); setEditingTemplate(null); }}>
                  <X size={16} />
                </Button>
              </div>
              <div className="space-y-3">
                <Input
                  value={(newTemplate || editingTemplate).name}
                  onChange={(e) => {
                    const setter = newTemplate ? setNewTemplate : setEditingTemplate;
                    setter(prev => ({ ...prev, name: e.target.value }));
                  }}
                  placeholder="Template name..."
                  className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  data-testid="template-name-input"
                />
                <Input
                  value={(newTemplate || editingTemplate).subject}
                  onChange={(e) => {
                    const setter = newTemplate ? setNewTemplate : setEditingTemplate;
                    setter(prev => ({ ...prev, subject: e.target.value }));
                  }}
                  placeholder="Subject line..."
                  className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  data-testid="template-subject-input"
                />
                <textarea
                  value={(newTemplate || editingTemplate).body}
                  onChange={(e) => {
                    const setter = newTemplate ? setNewTemplate : setEditingTemplate;
                    setter(prev => ({ ...prev, body: e.target.value }));
                  }}
                  placeholder="Email body..."
                  rows={8}
                  className={`w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#004E86] ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200'
                  }`}
                  data-testid="template-body-input"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => { setNewTemplate(null); setEditingTemplate(null); }}>Cancel</Button>
                  <Button onClick={handleSaveTemplate} disabled={isSavingTemplate} className="bg-[#004E86] hover:bg-[#003d6b] text-white" data-testid="save-template-btn">
                    {isSavingTemplate ? <Loader2 size={16} className="animate-spin mr-1" /> : <Save size={16} className="mr-1" />}
                    Save
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Default Follow-up Series */}
          <div>
            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              <Mail size={16} className="text-[#F2A900]" /> RV Match AI Follow-Up Series
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.defaults.map((t) => (
                <TemplateCard key={t.id} template={t} isDark={isDark} isDefault showDay onUseTemplate={handleUseTemplate} />
              ))}
            </div>
          </div>

          {/* Custom Templates */}
          <div>
            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              <FileText size={16} /> My Custom Templates
            </h3>
            {templates.custom.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} py-4 text-center`}>
                No custom templates yet. Create one to speed up your outreach!
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {templates.custom.map((t) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    isDark={isDark}
                    onEdit={() => setEditingTemplate({ ...t })}
                    onDelete={() => handleDeleteTemplate(t.id)}
                    onUseTemplate={handleUseTemplate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sent Emails Section */}
      {activeSection === 'drip' && isSuperAdmin && (
        <DripCampaignPanel isDark={isDark} />
      )}

      {activeSection === 'history' && (
        <SentEmailsViewer
          emails={recentEmails}
          isDark={isDark}
          formatDate={formatDate}
        />
      )}

      {/* Signature Section */}
      {activeSection === 'signature' && (
        <SignatureSection isDark={isDark} />
      )}

      {/* Domain Settings Section — platform-level, super admin only */}
      {activeSection === 'settings' && isSuperAdmin && (
        <DomainSettingsSection isDark={isDark} />
      )}
      </main>
    </div>
  );
};

const SignatureSection = ({ isDark }) => {
  const [signatureText, setSignatureText] = useState('');
  const [signatureImageUrl, setSignatureImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSignature();
  }, []);

  const fetchSignature = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/signature`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSignatureText(res.data.signature_text || '');
      setSignatureImageUrl(res.data.signature_image_url || '');
    } catch (err) {
      console.error('Error fetching signature:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = getAuthToken();
      await axios.put(`${API}/dealer/signature`, {
        signature_text: signatureText,
        signature_image_url: signatureImageUrl,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Signature saved successfully');
    } catch (err) {
      toast.error('Failed to save signature');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, GIF, or WebP image');
      return;
    }

    // Validate file size (500KB)
    if (file.size > 500 * 1024) {
      toast.error('Image too large. Maximum size is 500KB.');
      return;
    }

    setIsUploading(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/dealer/signature/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setSignatureImageUrl(res.data.image_url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    setSignatureImageUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-[#004E86]" size={36} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signature-section">
      {/* Header */}
      <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#004E86]/10 flex items-center justify-center">
            <PenTool size={20} className="text-[#004E86]" />
          </div>
          <div>
            <h2 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Signature</h2>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Your signature will be automatically added to all outgoing emails
            </p>
          </div>
        </div>

        {/* Signature Image Upload */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Signature Image (Logo or Photo)
          </label>
          <div className="flex items-start gap-4">
            {/* Image Preview */}
            <div className={`w-32 h-24 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden ${
              isDark ? 'border-gray-600 bg-gray-700/50' : 'border-gray-300 bg-gray-50'
            }`}>
              {signatureImageUrl ? (
                <img src={signatureImageUrl} alt="Signature" className="max-w-full max-h-full object-contain" />
              ) : (
                <Image size={24} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="signature-image-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-2"
                data-testid="upload-signature-image-btn"
              >
                {isUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {signatureImageUrl ? 'Change Image' : 'Upload Image'}
              </Button>
              {signatureImageUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeImage}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  data-testid="remove-signature-image-btn"
                >
                  <Trash2 size={14} className="mr-1" />
                  Remove
                </Button>
              )}
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                PNG, JPEG, GIF or WebP. Max 500KB.
              </p>
            </div>
          </div>
        </div>

        {/* Signature Text */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Signature Text
          </label>
          <textarea
            value={signatureText}
            onChange={(e) => setSignatureText(e.target.value)}
            placeholder="John Smith
Sales Manager
RV Match AI
(555) 123-4567
john@rvmatchai.com"
            rows={5}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${
              isDark 
                ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-500' 
                : 'bg-white border-gray-300 placeholder:text-gray-400'
            }`}
            data-testid="signature-text-input"
          />
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Each line will appear as a new line in your signature
          </p>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-[#004E86] hover:bg-[#003d6b] text-white"
          data-testid="save-signature-btn"
        >
          {isSaving ? <Loader2 size={16} className="animate-spin mr-1" /> : <Save size={16} className="mr-1" />}
          Save Signature
        </Button>
      </div>

      {/* Preview */}
      <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Preview</h3>
        <div className={`rounded-lg p-4 border ${isDark ? 'border-gray-700 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            This is how your signature will appear at the end of emails...
          </p>
          <div className={`pt-4 border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
            {signatureImageUrl && (
              <img 
                src={signatureImageUrl} 
                alt="Signature" 
                className="max-w-[200px] max-h-[100px] mb-2"
                data-testid="signature-preview-image"
              />
            )}
            {signatureText ? (
              <div className={`text-sm whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-testid="signature-preview-text">
                {signatureText}
              </div>
            ) : (
              <p className={`text-sm italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No signature text added yet
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DomainSettingsSection = ({ isDark }) => {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [senderName, setSenderName] = useState('');
  const [senderLocal, setSenderLocal] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/email-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(res.data);
      setSenderName(res.data.custom_sender_name || '');
      setSenderLocal(res.data.custom_sender_local || '');
      setSenderEmail(res.data.custom_sender_email || '');
    } catch (err) {
      console.error('Error fetching email settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const token = getAuthToken();
      const res = await axios.put(`${API}/dealer/email-settings`,
        { sender_name: senderName, sender_email: senderEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNameOnly = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const token = getAuthToken();
      const res = await axios.put(`${API}/dealer/email-settings`,
        { sender_name: senderName, sender_email: '', sender_local: senderLocal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update sender name');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevert = async () => {
    setIsSaving(true);
    try {
      const token = getAuthToken();
      await axios.put(`${API}/dealer/email-settings`,
        { sender_name: '', sender_email: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reverted to default sender');
      setSenderName('');
      setSenderEmail('');
      fetchSettings();
    } catch (err) {
      toast.error('Failed to revert settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#004E86]" size={32} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="domain-settings-section">
      {/* Current Status */}
      <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings?.is_using_custom_domain ? 'bg-green-100' : 'bg-yellow-100'}`}>
            <Globe size={20} className={settings?.is_using_custom_domain ? 'text-green-700' : 'text-yellow-700'} />
          </div>
          <div>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Email Sender Domain
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {settings?.is_using_custom_domain
                ? 'Verified domain — emails can go to any lead'
                : 'Using Resend test domain (limited)'}
            </p>
          </div>
        </div>

        <div className={`p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
          <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Current Sender</p>
          <p className={`text-sm font-mono ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            {settings?.is_using_custom_domain
              ? `${settings.custom_sender_name} <${settings.custom_sender_email}>`
              : settings?.default_sender}
          </p>
        </div>

        {!settings?.is_using_custom_domain && (
          <div className={`p-3 rounded-lg border-l-4 border-yellow-400 ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
            <p className={`text-sm font-medium ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
              Limited Mode
            </p>
            <p className={`text-xs mt-1 ${isDark ? 'text-yellow-200/70' : 'text-yellow-700'}`}>
              With the test domain, you can only send emails to your own account email. Set up a custom domain to email any lead.
            </p>
          </div>
        )}
      </div>

      {/* Sender Name Personalization — global platform email, per-dealer display name */}
      <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Personalize Sender Name
        </h3>
        <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Your emails send from the verified RV Match A.I. address — customize just the name your leads see in their inbox.
        </p>

        <div className="space-y-3 mb-5">
          <div>
            <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sender Name</label>
            <Input
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="e.g., Mike at Cliff's RV"
              className={isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}
              data-testid="sender-name-input"
              maxLength={80}
            />
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Leads will see this in their inbox as the &ldquo;From&rdquo; line.
            </p>
          </div>
          <div>
            <label className={`text-xs font-semibold block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sender Username (optional)</label>
            <div className="flex items-center gap-1">
              <Input
                value={senderLocal}
                onChange={(e) => setSenderLocal(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
                placeholder="mike"
                className={`${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''} max-w-[180px]`}
                data-testid="sender-local-input"
                maxLength={40}
              />
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                @rvmatchai.app
              </span>
            </div>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Leave blank to keep the platform default. Any username on @rvmatchai.app works — no verification needed.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSaveNameOnly}
              disabled={(!senderName.trim() && !senderLocal.trim()) || isSaving}
              className="bg-[#004E86] hover:bg-[#003d6b] text-white"
              data-testid="save-sender-name-btn"
            >
              {isSaving ? <Loader2 size={16} className="animate-spin mr-1" /> : <Save size={16} className="mr-1" />}
              Save
            </Button>
            {settings?.custom_sender_name && (
              <Button
                variant="outline"
                onClick={handleRevert}
                disabled={isSaving}
                className={isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700 bg-transparent' : ''}
                data-testid="revert-domain-btn"
              >
                Revert to Default
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Setup Instructions */}
      <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>How to Set Up Your Custom Domain</h3>
        {settings?.setup_instructions?.steps?.map((step, idx) => (
          <p key={idx} className={`text-sm mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'} ${step.startsWith('   ') ? 'ml-4' : ''}`}>
            {step}
          </p>
        ))}
        <div className="mt-4">
          <a
            href="https://resend.com/domains"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#004E86] hover:underline"
            data-testid="resend-domains-link"
          >
            <ExternalLink size={14} /> Open Resend Domain Settings
          </a>
        </div>

        {/* Benefits */}
        <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
          <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-green-400' : 'text-green-700'}`}>Benefits of Custom Domain:</p>
          {settings?.setup_instructions?.benefits?.map((b, idx) => (
            <p key={idx} className={`text-xs mb-0.5 ${isDark ? 'text-green-300/80' : 'text-green-700'}`}>
              {b}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

const TemplateCard = ({ template, isDark, isDefault, showDay, onEdit, onDelete, onUseTemplate }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`rounded-lg border p-3 transition-all overflow-hidden ${
        isDark
          ? 'border-white/10 bg-white/[0.04] hover:bg-white/[0.06]'
          : 'border-gray-200 bg-gray-50 hover:bg-white'
      }`}
      data-testid={`template-card-${template.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <button onClick={() => setExpanded(!expanded)} className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2 mb-1">
            {showDay && template.series_day !== undefined && (
              <span className="text-xs font-bold text-[#F2A900] shrink-0">Day {template.series_day}</span>
            )}
            <span className={`text-sm font-semibold truncate ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{template.name}</span>
          </div>
          <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{template.subject}</p>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {onUseTemplate && (
            <Button
              size="sm"
              onClick={() => onUseTemplate(template)}
              className="h-7 px-2.5 text-xs bg-[#F2A900] hover:bg-amber-400 text-black font-bold rounded-full shadow-none"
              data-testid={`use-template-${template.id}`}
            >
              <Send size={12} className="mr-1" />
              Use
            </Button>
          )}
          {!isDefault && onEdit && (
            <button
              onClick={onEdit}
              className={`p-1 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-[#004E86] hover:bg-gray-100'}`}
              data-testid={`edit-template-${template.id}`}
            >
              <Edit2 size={14} />
            </button>
          )}
          {!isDefault && onDelete && (
            <button
              onClick={onDelete}
              className={`p-1 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-red-400 hover:bg-white/10' : 'text-gray-400 hover:text-red-500 hover:bg-gray-100'}`}
              data-testid={`delete-template-${template.id}`}
            >
              <Trash2 size={14} />
            </button>
          )}
          <ChevronRight size={14} className={`text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>
      {expanded && (
        <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
          <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Subject:</p>
          <p className={`text-sm mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{template.subject}</p>
          <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Body:</p>
          <pre className={`text-xs whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {template.body}
          </pre>
          {onUseTemplate && (
            <div className="mt-3 pt-3 border-t border-dashed border-gray-300 dark:border-gray-600">
              <Button
                size="sm"
                onClick={() => onUseTemplate(template)}
                className="w-full bg-[#004E86] hover:bg-[#003d6b] text-white"
                data-testid={`use-template-expanded-${template.id}`}
              >
                <Send size={14} className="mr-2" />
                Use This Template
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailCenterTab;

const SentEmailsViewer = ({ emails, isDark, formatDate }) => {
  const [selectedId, setSelectedId] = React.useState(emails?.[0]?.id || null);
  const selected = emails.find(e => e.id === selectedId);

  if (!emails || emails.length === 0) {
    return (
      <div className={`rounded-xl shadow-sm overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="text-center py-12" data-testid="no-sent-emails">
          <Mail size={48} className={`mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>No emails sent yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-sm overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'} grid grid-cols-1 lg:grid-cols-[340px_1fr] min-h-[600px]`}>
      {/* List */}
      <div className={`border-r ${isDark ? 'border-gray-700' : 'border-gray-200'} max-h-[80vh] overflow-y-auto`} data-testid="sent-emails-list">
        {emails.map((email) => {
          const active = email.id === selectedId;
          return (
            <button
              key={email.id}
              onClick={() => setSelectedId(email.id)}
              data-testid={`sent-email-${email.id}`}
              className={`w-full text-left p-4 border-b transition-colors ${
                active
                  ? isDark ? 'bg-blue-900/30 border-gray-700' : 'bg-blue-50 border-gray-200'
                  : isDark ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                {email.status === 'opened'
                  ? <MailOpen size={14} className="text-green-500 shrink-0" />
                  : <CheckCircle2 size={14} className="text-blue-500 shrink-0" />}
                <p className={`text-sm font-semibold truncate flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {email.subject || '(no subject)'}
                </p>
                <span className={`text-[11px] shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatDate(email.sent_at)}
                </span>
              </div>
              <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                To: {email.lead_name ? `${email.lead_name} <${email.lead_email}>` : email.lead_email}
              </p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className={`text-[11px] inline-flex items-center gap-1 ${email.open_count > 0 ? 'text-green-600' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  <Eye size={11} /> {email.open_count > 0 ? `${email.open_count}x` : '0'}
                </span>
                {email.results_click_count > 0 && (
                  <span className="text-[11px] inline-flex items-center gap-1 text-blue-600">
                    <MousePointerClick size={11} /> {email.results_click_count}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Reading pane */}
      <div className={`p-5 sm:p-7 max-h-[80vh] overflow-y-auto ${isDark ? 'bg-gray-900' : 'bg-white'}`} data-testid="sent-email-reader">
        {selected ? (
          <>
            <h2 className={`text-xl sm:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="reader-subject">
              {selected.subject || '(no subject)'}
            </h2>
            <div className={`flex flex-wrap gap-x-5 gap-y-1 text-xs mb-4 pb-4 border-b ${isDark ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-200'}`}>
              <span><span className="font-semibold">To:</span> {selected.lead_name ? `${selected.lead_name} <${selected.lead_email}>` : selected.lead_email}</span>
              <span><span className="font-semibold">Sent:</span> {formatDate(selected.sent_at)}</span>
              <span className={`inline-flex items-center gap-1 ${selected.open_count > 0 ? 'text-green-500' : ''}`}>
                <Eye size={12} /> {selected.open_count > 0 ? `Opened ${selected.open_count}x` : 'Not opened yet'}
              </span>
              {selected.results_click_count > 0 && (
                <span className="inline-flex items-center gap-1 text-blue-500">
                  <MousePointerClick size={12} /> {selected.results_click_count} click{selected.results_click_count !== 1 ? 's' : ''}
                </span>
              )}
              {selected.first_opened_at && (
                <span>First opened: {formatDate(selected.first_opened_at)}</span>
              )}
            </div>

            {selected.body_html ? (
              // Render in a hard-forced white "letter" card so the email's own
              // inline colors (typically dark gray on white) render legibly,
              // regardless of the surrounding dashboard theme. Inline styles
              // beat any global dark-mode CSS overrides.
              <div
                className="rounded-xl overflow-x-auto shadow-inner border"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#111827',
                  borderColor: '#e5e7eb',
                  padding: '20px 24px',
                }}
                data-testid="reader-body-html-wrapper"
              >
                <div
                  className="prose prose-sm max-w-none"
                  style={{ color: '#111827' }}
                  dangerouslySetInnerHTML={{ __html: selected.body_html }}
                  data-testid="reader-body-html"
                />
              </div>
            ) : selected.body ? (
              <pre
                className="whitespace-pre-wrap font-sans text-sm leading-relaxed rounded-xl shadow-inner border"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#111827',
                  borderColor: '#e5e7eb',
                  padding: '20px 24px',
                }}
                data-testid="reader-body-text"
              >
                {selected.body}
              </pre>
            ) : (
              <p className={`text-sm italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(Email body not available)</p>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-64">
            <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Select an email to view its content</p>
          </div>
        )}
      </div>
    </div>
  );
};

const FolderButton = ({ id, label, active, isDark, onClick, badge, children }) => (
  <button
    onClick={onClick}
    className={`flex items-center shrink-0 gap-2 px-4 py-2.5 lg:py-2 min-h-[44px] lg:min-h-0 rounded-full lg:rounded-r-full lg:rounded-l-none lg:-ml-4 lg:pl-7 text-sm font-medium whitespace-nowrap transition-colors ${
      active
        ? isDark ? 'bg-blue-900/40 text-white font-semibold' : 'bg-[#fce8e6] text-[#c2410c] font-semibold'
        : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
    }`}
    data-testid={`email-section-${id}`}
  >
    {children}
    <span>{label}</span>
    {badge > 0 && (
      <span className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
        active
          ? isDark ? 'bg-white/20 text-white' : 'bg-white text-[#c2410c]'
          : isDark ? 'bg-blue-500/30 text-blue-200' : 'bg-[#c2410c] text-white'
      }`} data-testid="inbox-unread-count">
        {badge}
      </span>
    )}
  </button>
);
